import {
    Grid,
    Typography
} from '@mui/material';
import { SubmitButton } from '@tassat/button';
import { SelectOption, TransactionTypeChar } from '@tassat/common-definitions';
import {
    CurrencyField,
    SelectField,
    TextField
} from '@tassat/formik-input';
import { AccountWithdrawalDataRecord } from '@tassat/fred-definitions';
// import { LastTwoAssetTransactionsWidget } from '@tassat/text';
import { AddressBookDropdown, WithdrawalInstructionsOptionRenderer } from '@tassat/menu';
import { formatAsAsset } from '@tassat/utility';
import { Formik } from 'formik';
import moment from 'moment';
import * as React from 'react';

import { emptySelectOption } from 'Lib/constant';
import WithdrawalInstructionDetails from 'SharedComponents/withdrawalInstructions/withdrawalInstructionDetails';

import { validateWithdrawal } from './submission';
import {
    // eslint-disable-next-line unused-imports/no-unused-imports
    IFormErrors, IFormProps, IFormikProps, IWithdrawalFormValues
} from './types';

const FormContents = (props: IFormProps) => {
    const {
        classes,
        formProps,
        errors,
        values,
        isSubmitting,
        isValid,
        setFieldValue,
        submitForm
    } = props;

    const {
        accountPositionBalance,
        currentWallet,
        dxyObject,
        withdrawalInstructions,
        accountRelationships
    } = formProps;

    const accountPositionUSDBalance = accountPositionBalance[0].ccy[dxyObject?.AssetId];
    const allAccountRelationshipMapped = accountRelationships.filter((relationship) => relationship.AccountId === currentWallet.AccountId)
        .map(relationship => relationship.AccountWithdrawalDataId);

    const withdrawalInstructionsMapped: SelectOption[] = React.useMemo(
        () => withdrawalInstructions
            .filter((instruction) => allAccountRelationshipMapped.includes(instruction.AccountWithdrawalDataId))
            .map((instruction: AccountWithdrawalDataRecord) => ({
                label: instruction.Description,
                value: instruction.AccountWithdrawalDataId,
                refData: instruction
            })), [ allAccountRelationshipMapped, withdrawalInstructions ]
    );

    React.useEffect(() => {
        const isRecordDeleted = !withdrawalInstructionsMapped
            .some((instruction) => instruction.value === (values.WithdrawalInstructions?.value ?? 0));
        if (isRecordDeleted) {
            setFieldValue('WithdrawalInstructions', emptySelectOption);
        }
    }, [ withdrawalInstructionsMapped.length, values.WithdrawalInstructions?.value ]);

    return (
        <form className={classes.flexColumn}>
            <Grid
                container
                direction='row'
                justifyContent='space-around'
                spacing={0}
                wrap='nowrap'
            >
                <Grid
                    container
                    direction='column'
                    item
                    md={5}
                    spacing={0}
                    wrap='nowrap'
                    xs={12}
                >
                    <Grid
                        className={classes.fieldContainer}
                        container
                        direction='column'
                        item
                        spacing={0}
                        wrap='nowrap'
                        xs={12}
                    >
                        <Grid
                            container
                            direction='column'
                            spacing={0}
                            wrap='nowrap'
                        >
                            <Typography
                                className={classes.SelectAssetDropdownTitle}
                                variant='h5'
                            >
                                <strong>Withdrawal Amount</strong>
                            </Typography>
                            <CurrencyField
                                currencyDecimalPoint={2}
                                disabled={false}
                                errors={errors}
                                fieldSize='large'
                                label=''
                                name='WithdrawalAmount'
                                placeholder={dxyObject.Ticker}
                            />
                            {accountPositionUSDBalance && (
                                <Typography
                                    className={classes.AvailableBalance}
                                    variant='h5'
                                >
                                    {`Available ${dxyObject.Ticker} Balance: ${formatAsAsset(accountPositionUSDBalance.availableBalance, dxyObject, 'symbol')}`}
                                </Typography>
                            )}
                        </Grid>
                    </Grid>
                    <Grid
                        className={classes.fieldContainer}
                        container
                        direction='column'
                        item
                        spacing={0}
                        wrap='nowrap'
                        xs={12}
                    >
                        <TextField
                            errors={errors}
                            fieldSize='large'
                            label='Private Description (Optional)'
                            maxLength={40}
                            name='PrivateDescription'
                            values={values}
                        />
                    </Grid>
                </Grid>
                <Grid
                    container
                    direction='column'
                    item
                    md={5}
                    spacing={0}
                    wrap='nowrap'
                    xs={12}
                >
                    <Grid
                        className={classes.withdrawalFieldContainer}
                        item
                    >
                        <Typography
                            className={classes.SelectAssetDropdownTitle}
                            variant='h6'
                        >
                            <strong>Withdrawal Instructions</strong>
                        </Typography>
                        <SelectField
                            DropdownIndicatorRender={AddressBookDropdown}
                            errors={errors}
                            id='withdrawalInstructionSelect'
                            label=''
                            name='WithdrawalInstructions'
                            OptionRenderer={(props) => (
                                <WithdrawalInstructionsOptionRenderer
                                    {...props}
                                />
                            )}
                            options={withdrawalInstructionsMapped}
                            values={values}
                        />
                        {values.WithdrawalInstructions?.value && (
                            <>
                                <Grid
                                    className={classes.instructionDetailsContainer}
                                    container
                                >
                                    <WithdrawalInstructionDetails
                                        withdrawalInstruction={values.WithdrawalInstructions.refData}
                                    />
                                </Grid>
                            </>
                        )}
                    </Grid>
                </Grid>
            </Grid>
            <Grid
                className={classes.submitContainer}
                container
                direction='row'
                item
                justifyContent='center'
                spacing={0}
                wrap='nowrap'
                xs={12}
            >
                <SubmitButton
                    disabled={isSubmitting || !isValid}
                    onClick={submitForm}
                    text='WITHDRAW'
                    type='withdraw'
                />
            </Grid>
        </form>
    );
};

export default function WithdrawalForm(props: IFormikProps) {
    const {
        classes,
        formProps
    } = props;

    const {
        currentWallet,
        dxyObject,
        accountPositionBalance,
        handleWithdrawal
    } = formProps;

    const [initialValues] = React.useState<IWithdrawalFormValues>({
        Type: TransactionTypeChar.withdrawal,
        WithdrawalAmount: '',
        WithdrawalInstructions: emptySelectOption,
        PrivateDescription: '',
        NeedsApproval: false,
        RequestTime: null
    });

    if (!currentWallet) return <div />;

    const withdrawalFormOriginalValidations = async (values: IWithdrawalFormValues) => {
        const errors: IFormErrors = {} as IFormErrors;
        validateWithdrawal(values, {
            errors,
            dxyObject,
            accountPositionBalance
        });

        return errors;
    };

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={async (values: IWithdrawalFormValues) => {
                values.NeedsApproval = currentWallet.AccessControl;
                values.RequestTime = moment();
                if (handleWithdrawal) {
                    handleWithdrawal(values);
                }
            }}
            validate={withdrawalFormOriginalValidations}
            validateOnChange
            validateOnMount
        >
            {
                (formContentsProps: IFormProps) => (
                    <FormContents
                        {...formContentsProps}
                        classes={classes}
                        formProps={formProps}
                    />
                )
            }
        </Formik>
    );
}